// Merging two duplicate records (Cameron's card on OLYAA-131): a list's Merge
// action (exactly two rows selected) and a record page's "Merge with Another …"
// both end in Foundation's MergeRecordsDialog, which calls the object's
// POST /api/merge/<object> (apps/server/src/mergeRecords.mjs) on Merge. Contacts
// (MOTHER-154, contactMerge.ts) and accounts (MOTHER-167, accountMerge.ts) each
// describe only what is theirs in a MergeSpec; everything else is here, so the
// two behave the same. Admins only: Delivery - External never reaches these
// pages, and the server refuses anyone without `use_everything`.
import {
  type FilterOption,
  type MergeChoice,
  type MergeField,
  type MergeRecord,
  MergeRecordsDialog,
  RecordFormDialog,
} from '@olytic/foundation';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { ObjectKey } from '../navigation';
import { ObjectsApiError, type ObjectRow, parseRecordResponse, recordUrl } from './objectsApi';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** What one object's merge needs that is its own. */
export interface MergeSpec {
  object: ObjectKey;
  /** The route that merges one record into another. */
  url: string;
  /** The singular label, e.g. "Contact": the dialog's title and the search field. */
  singular: string;
  /** What merging does, as the dialog says it, in order. */
  consequences: string[];
  /** The record page's action, and its ⌘K entry: "Merge with Another Contact…". */
  withAnotherLabel: string;
  /**
   * The fields the dialog compares, in the record page's order, each formatted
   * as the page shows it: only those the server lets a merge take from the
   * other record. A derived field is never one: the spec shows it elsewhere.
   */
  fields: { key: string; format: (value: unknown) => string }[];
  /** The record page's label for a field (Title Case). */
  labelOf: (key: string) => string;
  /** A record as the dialog lists it: its name, and a subtitle that tells two of the same name apart. */
  recordOf: (row: ObjectRow) => MergeRecord;
  /** The kept record's name once merged, by the server's rule (mergedValues in mergeRecords.mjs). */
  mergedName: (survivor: ObjectRow, merged: ObjectRow, takeFromOther: string[]) => string;
}

export const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

export const textOf = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** A field's value as the page shows it, or null when it is empty. */
export function shown(format: (value: unknown) => string, value: unknown): string | null {
  return isEmpty(value) ? null : format(value);
}

/** The two records' fields for the dialog, labelled and formatted as their record pages show them. */
export function mergeFieldsOf(spec: MergeSpec, first: ObjectRow, second: ObjectRow): MergeField[] {
  return spec.fields.map(({ key, format }) => ({
    key,
    label: spec.labelOf(key),
    first: shown(format, first[key]),
    second: shown(format, second[key]),
  }));
}

/**
 * The kept record with `keys` merged by the server's rule: a field in
 * takeFromOther takes the other's value; an empty one is filled from the other.
 */
export function mergedRow(survivor: ObjectRow, merged: ObjectRow, takeFromOther: string[], keys: string[]): ObjectRow {
  const row: ObjectRow = { id: survivor.id };
  for (const key of keys) {
    row[key] = takeFromOther.includes(key) || isEmpty(survivor[key]) ? merged[key] ?? null : survivor[key];
  }
  return row;
}

const plural = (spec: MergeSpec) => `${spec.singular.toLowerCase()}s`;

/** POST the merge. A refusal (400, 404, 409) throws the server's message. */
export async function postMerge(
  spec: MergeSpec,
  fetcher: Fetch,
  body: { survivorId: string; mergedId: string; takeFromOther: string[] },
): Promise<void> {
  const response = await fetcher(spec.url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (response.ok) return;
  let message = `The ${plural(spec)} could not be merged (${response.status}).`;
  try {
    const payload: unknown = await response.json();
    if (typeof payload === 'object' && payload !== null && typeof (payload as { error?: unknown }).error === 'string') {
      const error = (payload as { error: string }).error;
      if (error !== '') message = error;
    }
  } catch {
    // Not JSON: keep the fallback.
  }
  throw new ObjectsApiError(response.status, message);
}

/**
 * A list's selection-bar action: Merge, offered only while exactly two rows
 * are selected. `canMerge` is whether this object merges and the person may
 * delete its records (a merge deletes one).
 */
export function mergeActions(
  canMerge: boolean,
  selectedIds: string[],
  onMerge: (ids: [string, string]) => void,
): { label: string; onSelect: (ids: string[]) => void }[] {
  if (!canMerge || selectedIds.length !== 2) return [];
  return [{ label: 'Merge', onSelect: (ids) => { if (ids.length === 2) onMerge([ids[0]!, ids[1]!]); } }];
}

const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message.length > 0 ? error.message : fallback;

interface RecordMergeDialogProps {
  spec: MergeSpec;
  fetcher: Fetch;
  /** The two records, the first preselected as the one kept; null when closed. */
  ids: [string, string] | null;
  onClose: () => void;
  /** The merge succeeded: the record kept, and its name now. */
  onMerged: (survivorId: string, name: string) => void;
  /** The two records could not be loaded; the dialog has closed. */
  onFailed: (message: string) => void;
}

/**
 * Loads the two records and opens the merge dialog over them. Merge sends one
 * POST; a second click while it runs sends nothing. A refusal stays in the
 * dialog as its error.
 */
export function RecordMergeDialog({ spec, fetcher, ids, onClose, onMerged, onFailed }: RecordMergeDialogProps): JSX.Element | null {
  const [records, setRecords] = useState<[ObjectRow, ObjectRow] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set before the request starts, so a click before the re-render is still refused.
  const inFlight = useRef(false);
  const first = ids?.[0];
  const second = ids?.[1];
  const object = spec.object;

  useEffect(() => {
    setRecords(null);
    setError(null);
    if (first === undefined || second === undefined) return undefined;
    let live = true;
    Promise.all([first, second].map(async (id) => parseRecordResponse(await fetcher(recordUrl(object, id))))).then(
      ([a, b]) => { if (live) setRecords([a!, b!]); },
      (failure: unknown) => { if (live) onFailed(messageOf(failure, `The ${plural(spec)} could not be loaded.`)); },
    );
    return () => { live = false; };
    // onFailed and spec are the host's; a new one does not reload the records.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, object, first, second]);

  if (!records || !ids) return null;

  async function confirm(choice: MergeChoice) {
    if (inFlight.current || !records) return;
    const survivor = records.find((r) => r.id === choice.survivorId);
    const merged = records.find((r) => r.id !== choice.survivorId);
    if (!survivor || !merged) return;
    // Only what the spec offers may be taken: never a derived field.
    const takeable = new Set(spec.fields.map((field) => field.key));
    const takeFromOther = choice.takeFromOther.filter((key) => takeable.has(key));
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await postMerge(spec, fetcher, { survivorId: survivor.id, mergedId: merged.id, takeFromOther });
      onMerged(survivor.id, spec.mergedName(survivor, merged, takeFromOther));
    } catch (failure) {
      setError(messageOf(failure, `The ${plural(spec)} could not be merged.`));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <MergeRecordsDialog
      open
      objectLabel={spec.singular}
      records={[spec.recordOf(records[0]), spec.recordOf(records[1])]}
      fields={mergeFieldsOf(spec, records[0], records[1])}
      consequences={spec.consequences}
      submitting={submitting}
      error={error}
      onConfirm={(choice) => void confirm(choice)}
      onCancel={onClose}
    />
  );
}

/** A record search that leaves out `excludedId`. */
export function excluding(
  load: (query: string) => Promise<FilterOption[]>,
  excludedId: string,
): (query: string) => Promise<FilterOption[]> {
  return async (query) => (await load(query)).filter((option) => option.value !== excludedId);
}

interface MergeWithAnotherProps {
  spec: MergeSpec;
  fetcher: Fetch;
  /** The record whose page this is: first in the dialog, and left out of the search. */
  recordId: string;
  open: boolean;
  onClose: () => void;
  /** The search the object's other lookup fields use (loadLookupOptions(object)). */
  search: (query: string) => Promise<FilterOption[]>;
  onMerged: (survivorId: string, name: string) => void;
  onFailed: (message: string) => void;
}

/** A record page's "Merge with Another …": find the other record, then the merge dialog with this one first. */
export function MergeWithAnother({
  spec, fetcher, recordId, open, onClose, search, onMerged, onFailed,
}: MergeWithAnotherProps): JSX.Element {
  const [other, setOther] = useState<string | null>(null);
  useEffect(() => { if (!open) setOther(null); }, [open]);
  const close = () => { setOther(null); onClose(); };
  const noun = spec.singular.toLowerCase();
  const fields = useMemo(() => [{
    key: noun,
    label: spec.singular,
    required: true,
    editor: { kind: 'lookup' as const, loadOptions: excluding(search, recordId) },
    help: `The duplicate of this ${noun}. You choose which one to keep next.`,
  }], [search, recordId, noun, spec.singular]);
  return (
    <>
      <RecordFormDialog
        open={open && other === null}
        onOpenChange={(next) => { if (!next) close(); }}
        title={spec.withAnotherLabel.replace('…', '')}
        fields={fields}
        submitLabel="Next"
        onSubmit={(values) => {
          const picked = values[noun] ?? null;
          if (picked === null || picked === recordId) throw new Error(`Choose a different ${noun}.`);
          setOther(picked);
        }}
      />
      <RecordMergeDialog
        spec={spec}
        fetcher={fetcher}
        ids={open && other !== null ? [recordId, other] : null}
        onClose={close}
        onMerged={onMerged}
        onFailed={(message) => { close(); onFailed(message); }}
      />
    </>
  );
}
